import { useState, useRef, useEffect } from "react"
import { Form } from "react-bootstrap"
import { Engagement } from "@models/engagement.model.ts"
import { EngagementService } from "@services/engagements/engagementService.ts"
import { engagementStore } from "../engagementStore.ts"
import { engagementEvents } from "../engagementEvents.ts"

export enum EngagementStatus {
  Staging = "STAGING",
  Serving = "SERVING",
  Paused = "PAUSED",
  Closed = "CLOSED",
}

const statusLabel: Record<string, string> = {
  [EngagementStatus.Staging]: "Staging",
  [EngagementStatus.Serving]: "Serving",
  [EngagementStatus.Paused]: "Paused",
  [EngagementStatus.Closed]: "Closed",
}


const statusDotColor: Record<string, string> = {
  [EngagementStatus.Staging]: "#6366f1",
  [EngagementStatus.Serving]: "#22c55e",
  [EngagementStatus.Paused]: "#eab308",
  [EngagementStatus.Closed]: "#9ca3af",
}

const statusTextColor: Record<string, string> = {
  [EngagementStatus.Staging]: "#4338ca",
  [EngagementStatus.Serving]: "#15803d",
  [EngagementStatus.Paused]: "#a16207",
  [EngagementStatus.Closed]: "#6b7280",
}

const statusBorderColor: Record<string, string> = {
  [EngagementStatus.Staging]: "#c7d2fe",
  [EngagementStatus.Serving]: "#bbf7d0",
  [EngagementStatus.Paused]: "#fef08a",
  [EngagementStatus.Closed]: "#e5e7eb",
}

const statusBgColor: Record<string, string> = {
  [EngagementStatus.Staging]: "#eef2ff",
  [EngagementStatus.Serving]: "#f0fdf4",
  [EngagementStatus.Paused]: "#fefce8",
  [EngagementStatus.Closed]: "#f9fafb",
}

export const EngagementStatusBadge = ({ engagement }: { engagement: Engagement }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [pendingStatus, setPendingStatus] = useState(engagement.status)
  const selectRef = useRef<HTMLSelectElement>(null)

  const status = engagement.status || EngagementStatus.Staging
  const label = statusLabel[status] || status

  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isEditing])

  const handleConfirm = async (newStatus: string) => {
    if (newStatus === status) {
      setIsEditing(false)
      return
    }
    try {
      const service = new EngagementService()
      await service.updateEngagementStatus(engagement.id, newStatus)
      engagementStore.selectedEngagement = {
        ...engagementStore.selectedEngagement,
        status: newStatus,
      } as any
      engagementEvents.send_event("ENGAGEMENT_STATUS_UPDATED")
    } catch (error) {
      console.error("Error updating engagement status:", error)
    } finally {
      setIsEditing(false)
    }
  }

  const handleCancel = () => {
    setPendingStatus(status)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleConfirm(pendingStatus)
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className="d-flex align-items-center gap-1 ms-auto me-2">
        <Form.Select
          ref={selectRef}
          size="sm"
          value={pendingStatus}
          onChange={(e) => setPendingStatus(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => handleCancel()}
          style={{ width: 140 }}
        >
          {Object.values(EngagementStatus).map((s) => (
            <option key={s} value={s}>
              {statusLabel[s]}
            </option>
          ))}
        </Form.Select>
        <i
          className="bi bi-check fs-20 text-success cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault()
            handleConfirm(pendingStatus)
          }}
        />
        <i
          className="bi bi-x fs-20 text-danger cursor-pointer"
          onMouseDown={(e) => {
            e.preventDefault()
            handleCancel()
          }}
        />
      </div>
    )
  }

  return (
    <div
      className="d-flex align-items-center gap-1 ms-auto me-2"
      style={{
        cursor: "pointer",
        padding: "4px 12px",
        borderRadius: 8,
        border: `1.5px solid ${statusBorderColor[status] || "#e5e7eb"}`,
        background: statusBgColor[status] || "#f9fafb",
        fontSize: 12,
        fontWeight: 500,
        color: statusTextColor[status] || "#6b7280",
      }}
      onDoubleClick={() => {
        setPendingStatus(status)
        setIsEditing(true)
      }}
      title="Double-click to edit status"
    >
      <span
        className="d-inline-block rounded-circle"
        style={{
          width: 7,
          height: 7,
          backgroundColor: statusDotColor[status] || "#9ca3af",
        }}
      />
      {label}
    </div>
  )
}
